import Image from "next/image";
import Link from "next/link";
import type { IconType } from "react-icons";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { getProgramImageMeta } from "@/content/program-images";

interface ProgramPreviewCardProps {
  slug: string;
  title: string;
  description: string;
  features?: string[];
  image?: string;
  icon: IconType;
  iconColor: string;
  className?: string;
}

export function ProgramPreviewCard({
  slug,
  title,
  description,
  features = [],
  image,
  icon: Icon,
  iconColor,
  className,
}: ProgramPreviewCardProps) {
  const meta = getProgramImageMeta(slug);
  const src = image ?? meta.src;
  const alt = meta.alt || title;
  const visibleFeatures = features.filter(Boolean).slice(0, 4);

  return (
    <article
      className={cn(
        "group relative flex h-full flex-col rounded-2xl border border-border bg-surface-elevated shadow-sm",
        "transition-all duration-500 ease-[var(--ease-out-expo)]",
        "hover:-translate-y-2.5 hover:shadow-xl hover:border-primary/30 hover:ring-1 hover:ring-primary/20",
        className
      )}
    >
      <div className="relative h-[11.5rem] w-full shrink-0 overflow-hidden rounded-t-2xl bg-card">
        <Image
          src={src}
          alt={alt}
          fill
          className="object-cover transition-transform duration-500 ease-[var(--ease-out-expo)] group-hover:scale-[1.08]"
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
        />
      </div>
      <div
        className={cn(
          "absolute left-5 top-[calc(11.5rem-1.375rem)] z-20 flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg ring-4 ring-surface-elevated",
          iconColor
        )}
      >
        <Icon size={18} aria-hidden />
      </div>
      <div className="relative z-10 flex flex-grow flex-col rounded-b-2xl px-5 pb-5 pt-8">
        <h3 className="mb-2 text-lg font-bold text-foreground">{title}</h3>
        <p className="mb-3 flex-grow text-sm leading-relaxed text-muted line-clamp-3">
          {description}
        </p>
        {visibleFeatures.length > 0 ? (
          <ul className="mb-4 space-y-1.5">
            {visibleFeatures.map((feature) => (
              <li
                key={feature}
                className="flex items-start gap-1.5 text-xs leading-snug text-foreground/85"
              >
                <span className="mt-0.5 text-primary" aria-hidden>
                  ✓
                </span>
                <span className="line-clamp-1">{feature}</span>
              </li>
            ))}
          </ul>
        ) : null}
        <Link
          href={`/programs/${slug}`}
          className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-primary transition-colors hover:text-accent"
        >
          Learn More{" "}
          <ArrowRight
            size={14}
            className="transition-transform duration-300 group-hover:translate-x-1"
          />
        </Link>
      </div>
    </article>
  );
}
